import { getPhoto, type Photo, type Point } from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A place's picture from Wikipedia, as a booking site heads a place with one: the page nearest
 * it with a picture, else its name's, credited under it; nothing while there is none.
 */
export function PlacePhoto({ place }: { place: Point & { name: string } }) {
  const { t } = useTranslation();
  // Kept by the place it is of, so another place's is not shown while its own is asked.
  const [found, setFound] = useState<{ key: string; photo: Photo | null } | null>(null);
  const { name, lat, lon } = place;
  const key = `${name} ${String(lat)} ${String(lon)}`;

  useEffect(() => {
    let live = true;
    getPhoto(name, { lat, lon }).then(
      (photo) => {
        if (live) setFound({ key: `${name} ${String(lat)} ${String(lon)}`, photo });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [name, lat, lon]);

  const photo = found?.key === key ? found.photo : null;
  if (photo === null) return null;
  return (
    <figure className="place-photo">
      <img src={photo.image} alt={photo.title} loading="lazy" />
      <figcaption>
        <a href={photo.page} target="_blank" rel="noreferrer">
          {t('city.photoCredit', { title: photo.title })}
        </a>
      </figcaption>
    </figure>
  );
}
